import { useState, useEffect, useCallback, useMemo } from 'react';
import { getLeads } from '../api/leads-api';
import type { Lead, LeadsQueryParams } from '../types';

/**
 * Hook for fetching and managing leads list
 */
export function useLeads(params?: LeadsQueryParams) {
  const [data, setData] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize the params to avoid unnecessary re-renders
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const fetchLeads = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const leads = await getLeads(params);
      setData(leads);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch leads');
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchLeads();
  }, [paramsKey, fetchLeads]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchLeads,
  };
}
